/**
 * "How to put it in place" for one control: steps from public standards and CIS Benchmarks, the
 * Microsoft 365 services that can deliver it, what National already provides, and the evidence to keep.
 * Shown on the guidance page and the firm's control page, so no firm waits for National guidance.
 */
import { templatesForControl } from '@grc/shared/policies';
import { AVAILABILITY, COVERAGE_LEVEL, IMPLEMENTATION, IMPLEMENTATION_STATUS, MICROSOFT_SERVICES, servicesForControl, STANDARDS } from '@grc/shared/vocabulary';
import { Link } from 'react-router-dom';
import { AnswerSection } from './components';
import { showDraftLabel } from './config';

/**
 * National services that help with one control, strongest first, with who can use each (national-services.ts).
 * A tinted panel with a growth-green accent, so what National already provides stands apart (Andres, 2026-09-25).
 */
export function NationalServicesFor({ controlId }: { controlId: string }) {
  const rows = servicesForControl(controlId);
  if (!rows.length) return null;
  return (
    <div className="national-help">
      <h3>National services that help</h3>
      <ul className="plain national-for">
        {rows.map((r) => (
          <li key={r.serviceId}>
            <span className="national-for-head">
              <strong>{r.service.name}</strong>
              <span className={`cov-chip cov-${r.level}`}>{COVERAGE_LEVEL[r.level].label}</span>
            </span>
            <span className="small">{r.how}</span>
            <span className="national-for-who small">
              <span className="muted">Who can use it:</span> {AVAILABILITY[r.service.availability].label}
            </span>
          </li>
        ))}
      </ul>
      <p className="small">
        <Link to="/guidance/resources">All National services</Link>
      </p>
    </div>
  );
}

export function ImplementationGuide({ controlId, published, firmCode }: { controlId: string; published?: { guidance: string | null; successSummary?: string | null; benchmarkUrl?: string | null }; firmCode?: string }) {
  const g = IMPLEMENTATION[controlId];
  const policies = templatesForControl(controlId);
  if (!g && !published?.guidance) return null;
  return (
    // 5E NEXT PASS 1 (E5): the shared AnswerSection, so the question reads as every other question on the page.
    <AnswerSection id={`impl-${controlId}`} level={2} className="implementation" question="How do I put it in place?">
      {published?.guidance && (
        <div className="impl-published">
          <h3>National guidance</h3>
          <p>{published.guidance}</p>
          {published.successSummary && (
            <p>
              <strong>What success looks like:</strong> {published.successSummary}
            </p>
          )}
          {published.benchmarkUrl && (
            <p>
              <a href={published.benchmarkUrl} target="_blank" rel="noreferrer">
                CIS Benchmark reference
              </a>
            </p>
          )}
        </div>
      )}
      {g && (
        <>
          <ol className="impl-steps">
            {g.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <div className="impl-grid">
            {g.microsoft.length > 0 && (
              <div>
                <h3>Microsoft 365 services that can do this</h3>
                <ul className="plain impl-list">
                  {g.microsoft.map((k) => (
                    <li key={k}>
                      <a href={MICROSOFT_SERVICES[k].url} target="_blank" rel="noreferrer">
                        {MICROSOFT_SERVICES[k].name}
                      </a>
                      <span className="muted small"> {MICROSOFT_SERVICES[k].what}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <NationalServicesFor controlId={controlId} />
          </div>
          <p className="small">
            <strong>Evidence to keep:</strong> {g.evidence}
          </p>
          {policies.length > 0 && (
            <p className="small">
              <strong>Policy template:</strong>{' '}
              {policies.map((t, i) => (
                <span key={t.id}>
                  <Link to={firmCode ? `/firm/${firmCode}/policies?template=${t.id}` : '/guidance/policies'}>{t.title}</Link>
                  {i < policies.length - 1 ? ', ' : ''}
                </span>
              ))}
            </p>
          )}
          <p className="muted small">
            Based on{' '}
            {g.standards.map((k, i) => (
              <span key={k}>
                <a href={STANDARDS[k].url} target="_blank" rel="noreferrer">
                  {STANDARDS[k].title}
                </a>
                {i < g.standards.length - 1 ? ', ' : '.'}
              </span>
            ))}{' '}
            {showDraftLabel(IMPLEMENTATION_STATUS) && 'Draft National guidance, open for review. '}Your firm chooses the tools; any equivalent product that meets the control works.
          </p>
        </>
      )}
    </AnswerSection>
  );
}
